/* OKKO B2B — Quick Order: bozza d'ordine da incolla-testo o file CSV.
 *
 * Replica del flusso dell'app di riferimento (B2B Bulk Order / CSV Upload) senza
 * backend: l'indice SKU->variante arriva dalla vista-sonda ?view=sku (stesso
 * pattern del filtro Modelli), quindi il matching di qualsiasi file e' locale e
 * istantaneo — nessuna richiesta per-riga. L'esito NON va dritto al carrello:
 * riempie la bozza persistente e apre il Riepilogo (editabile) per la revisione;
 * l'invio resta l'ATC globale col suo controllo giacenza pre-invio.
 *
 * Dallo stesso indice si genera anche il "catalogo CSV" scaricabile (SKU, articolo,
 * prezzo, disponibili, colonna Quantita' vuota da compilare e re-importare).
 *
 * Fuori scopo dichiarato: barcode/UPC (rimosso su richiesta) e OCR di PDF/immagini
 * (serve un backend).
 */

(() => {
  'use strict';

  const money = (cents) => (window.OKKO && window.OKKO.money
    ? window.OKKO.money(cents)
    : `\u20ac${(cents / 100).toFixed(2)}`);

  const esc = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

  // --- Contesto tema: aggancia i fetch alla BOZZA in anteprima ---------------
  // Le viste-sonda (?view=sku/indice) e /products/*.js vivono solo nel tema che
  // le definisce. In anteprima di un tema NON pubblicato, un fetch "nudo" cade
  // sul tema LIVE (che quei template non li ha) -> HTML al posto del JSON ->
  // "Impossibile leggere la collezione". Portiamo preview_theme_id su ogni URL
  // interna finche' non siamo sul tema pubblicato (role 'main').
  const previewSuffix = (() => {
    const el = document.querySelector('[data-okko-theme-id]');
    const id = el && el.getAttribute('data-okko-theme-id');
    const role = el && el.getAttribute('data-okko-theme-role');
    return (id && role && role !== 'main') ? `preview_theme_id=${id}` : '';
  })();
  /** Aggiunge preview_theme_id alle URL interne quando siamo in anteprima bozza. */
  const okkoUrl = (path) => (previewSuffix
    ? `${path}${path.includes('?') ? '&' : '?'}${previewSuffix}`
    : path);

  // Le viste-sonda (view=sku/indice) sono PESANTI: sul B2B (~6000 varianti) il
  // rendering Liquid CONCORRENTE sfora i limiti e inietta "Liquid error" DENTRO il
  // JSON -> JSON.parse fallisce ("Impossibile leggere la collezione"). Per questo
  // le pagine si scaricano in SEQUENZA (mai Promise.all), e questo helper ritenta
  // una volta se il corpo non e' JSON valido (errore intermittente sotto carico).
  async function fetchJson(url, retries = 1) {
    for (let attempt = 0; ; attempt += 1) {
      let text = null;
      try { text = await (await fetch(url)).text(); }
      catch (e) { if (attempt >= retries) throw e; }
      if (text != null) {
        try { return JSON.parse(text); }
        catch (e) { if (attempt >= retries) throw e; }
      }
      await new Promise((r) => setTimeout(r, 350)); // respiro prima del retry
    }
  }

  // --- Indice SKU -> variante (vista-sonda, cache di sessione) ---------------

  // Cache a SESSIONE dell'indice: sopravvive alle navigazioni dello stesso tab e si
  // azzera alla chiusura. Evita di riscaricare ~1,9MB / rifare ~15s di render Liquid
  // a ogni pagina. In cache va solo l'indice base (snapshot di sessione, come lo
  // stock); i prezzi BSS restano live (letti da cpRules al momento del download).
  const ssGet = (k) => { try { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return null; } };
  const ssSet = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* quota piena: resta solo in-memory */ } };

  const indexCache = new Map(); // handle collezione -> promise dell'indice

  function skuIndex(handle = 'all') {
    if (indexCache.has(handle)) return indexCache.get(handle);
    const ssKey = `okko-sku-index:${handle}`;
    const promise = (async () => {
      const cached = ssGet(ssKey);
      if (cached) return new Map(cached); // [[sku, entry], ...] gia' pronto, 0 fetch
      const url = (page) => okkoUrl(`/collections/${handle}?view=sku${page > 1 ? `&page=${page}` : ''}`);
      const first = await fetchJson(url(1));
      // SEQUENZIALE (una pagina alla volta): il fetch parallelo di queste pagine
      // pesanti mandava Liquid in errore intermittente e corrompeva il JSON.
      // Cap 30 (era 12): la sonda ora pagina by 100, le pagine sono ~2.5x.
      // Una pagina ROTTA (Liquid error deterministico, visto il 2026-07-27) non
      // affonda piu' tutto l'indice: si salta e si conta, l'indice esce PARZIALE
      // (map.parziale = pagine perse) e chi lo consuma avvisa l'utente.
      const chunks = [first];
      const perse = [];
      for (let page = 2; page <= Math.min(first.pages, 30); page += 1) {
        try { chunks.push(await fetchJson(url(page))); }
        catch (e) { perse.push(page); }
      }
      const norm = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
      const map = new Map();
      chunks.forEach((chunk) => chunk.items.forEach(([sku, id, price, max, title, prodUrl, img, marca, modelli, listino, vt, pid, cf, pre]) => {
        if (!sku) return;
        // `t` = titolo del PRODOTTO, `v` = variante: la sonda non li concatena piu'
        // col trattino, cosi' le modali possono rendere la variante come pillola.
        // `pid` = id PRODOTTO: serve al ponte BSS per scontare l'export (regole per
        // collezione/tag risolte da search.bss.b2b via prezzoVariante).
        map.set(String(sku).trim().toUpperCase(), {
          id, pid, p: price, c: listino === undefined ? null : listino, m: max, t: title, v: vt || '', s: sku, u: prodUrl, i: img,
          b: norm(marca), mm: norm(modelli),
          cf: cf > 1 ? cf : 0, // confezione (multiplo d'ordine); assente nelle cache di sessione vecchie -> 0
          pre: pre || 0, // pre-order (data '%d/%m' o 1); assente nelle cache vecchie -> 0
        });
      }));
      if (perse.length) {
        // Indice incompleto: NIENTE cache di sessione (una navigazione futura
        // ritenta l'indice pieno), solo il flag per gli avvisi a valle.
        map.parziale = perse.length;
      } else {
        ssSet(ssKey, [...map]); // persisti per la sessione: navigazioni successive = 0 fetch
      }
      return map;
    })();
    promise.catch(() => { indexCache.delete(handle); }); // errore: si riprova alla prossima
    indexCache.set(handle, promise);
    return promise;
  }

  // --- Metadati BSS per lo sconto di DOWNLOAD e ANTEPRIMA (mai per il live-site) ---
  // Cache a SESSIONE di tag + collezioni per prodotto (da search.bss.b2b). Serve solo
  // a download e paste-preview per risolvere le regole -- INCLUSE quelle per-collezione,
  // che il vecchio /products/.js non copriva -- via la funzione pubblica scontaPrezzo,
  // che usa le cpRules LIVE. Il pricing del SITO non passa di qui: resta nel ponte,
  // sempre fresco (come BSS stesso, che ricalcola a ogni pagina). pid dall'indice SKU.
  const META = new Map(); // pid -> { t: tags, c: collezioni (id) }
  let metaHydrated = false;
  function metaHydrate() {
    if (metaHydrated) return;
    metaHydrated = true;
    let blob = null;
    try { const v = sessionStorage.getItem('okko-bss-meta'); blob = v ? JSON.parse(v) : null; } catch (e) { return; }
    if (blob) Object.keys(blob).forEach((pid) => { if (!META.has(pid)) META.set(pid, blob[pid]); });
  }
  function metaSave() {
    const blob = {}; META.forEach((v, pid) => { blob[pid] = v; });
    try { sessionStorage.setItem('okko-bss-meta', JSON.stringify(blob)); } catch (e) { /* quota: resta in-memory */ }
  }
  // Assicura tag+collezioni per i pid richiesti (cache a sessione + fetch dei mancanti).
  async function bssMeta(pids) {
    metaHydrate();
    const mancanti = [...new Set(pids.map(String))].filter((pid) => pid && pid !== 'null' && !META.has(pid));
    for (let i = 0; i < mancanti.length; i += 40) {
      const q = mancanti.slice(i, i + 40).map((id) => `id:${id}`).join(' OR ');
      let arr;
      try { arr = await fetch(okkoUrl(`/search.js?q=${encodeURIComponent(q)}&view=bss.b2b`)).then((r) => r.json()); }
      catch (e) { continue; }
      (arr || []).forEach((p) => META.set(String(p.id), { t: p.tags || [], c: (p.collections || []).map(String) }));
    }
    if (mancanti.length) metaSave();
    return META;
  }

  async function scontaRighe(list) {
    const bss = window.OKKO && window.OKKO.bss;
    if (!bss) return;
    try { await bss.attendiRegole(); } catch (e) { return; }
    if (!bss.pronto) return;
    // L'Anteprima e' una superficie che il cliente guarda -> percorso LIVE del ponte,
    // dati FRESCHI (assicuraProdotti fetcha a blocchi search.bss.b2b, prezzoVariante da'
    // lo scontato, anche per-collezione). NON la cache okko-bss-meta (quella resta al
    // solo Scarica catalogo, che e' un export).
    const ok = list.filter((r) => r.state === 'ok' && r.e);
    // pid: c'e' gia' sulle righe da INCOLLA (indice SKU); il RIORDINO da Ordini costruisce
    // le righe a mano senza pid -> recuperalo da /products/<handle>.js (come la wishlist).
    await Promise.all(ok.map(async (r) => {
      if (r.e.pid || !r.e.u) return;
      const handle = String(r.e.u).split('/products/')[1];
      if (!handle) return;
      try { const p = await fetch(`/products/${handle.split('?')[0]}.js`).then((x) => x.json()); if (p) r.e.pid = p.id; } catch (e) { /* noop */ }
    }));
    const conPid = ok.filter((r) => r.e.pid);
    if (bss.assicuraProdotti) await bss.assicuraProdotti(conPid.map((r) => r.e.pid));
    conPid.forEach((r) => {
      const v = bss.prezzoVariante(r.e.id);
      if (v && v.p < r.e.p) { r.e.op = r.e.p; r.e.p = v.p; }
    });
  }

  // --- Parsing: separatori liberi, intestazione riconosciuta, nota in 3a colonna

  function splitLine(line) {
    const strip = (cell) => String(cell).trim().replace(/^"(.*)"$/, '$1').trim();
    let parts;
    if (line.includes('\t')) parts = line.split('\t');
    else if (line.includes(';')) parts = line.split(';');
    else if (line.includes(',')) parts = line.split(',');
    else {
      const words = line.trim().split(/\s+/);
      parts = [words[0], words[1] || '', words.slice(2).join(' ')];
    }
    return parts.map(strip);
  }

  function parseInput(text) {
    const lines = String(text).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    let cols = { sku: 0, qty: 1 };
    let start = 0;
    const head = splitLine(lines[0]).map((c) => c.toLowerCase());
    if (head.some((c) => c.includes('sku') || c.includes('codice'))) {
      cols = {
        sku: head.findIndex((c) => c.includes('sku') || c.includes('codice')),
        qty: head.findIndex((c) => c.includes('quant') || c === 'qty'),
      };
      if (cols.qty === -1) cols.qty = cols.sku + 1;
      start = 1;
    }
    const bySku = new Map();
    for (let i = start; i < lines.length; i += 1) {
      const parts = splitLine(lines[i]);
      const sku = (parts[cols.sku] || '').toUpperCase();
      if (!sku) continue;
      const qty = parseInt(parts[cols.qty] || '', 10);
      const prev = bySku.get(sku);
      if (prev) {
        // stesso SKU su piu' righe del file: le quantita' si sommano
        if (Number.isFinite(qty) && Number.isFinite(prev.req)) prev.req += qty;
      } else {
        bySku.set(sku, { sku, req: Number.isFinite(qty) ? qty : NaN });
      }
    }
    return [...bySku.values()];
  }

  // --- Stato e modale ---------------------------------------------------------

  let dialog = null;
  let rows = []; // working set dell'anteprima
  let indiceParziale = 0; // pagine della sonda SKU perse nell'ultimo indice (0 = completo)
  let previewOrigine = null; // 'ordini' = anteprima aperta dal riordino (Indietro/Svuota tornano li')

  function ensureDialog() {
    if (dialog) return dialog;
    // guscio server-rendered dal gruppo Strumenti (okko-strumento-quickorder), se c'e'
    dialog = document.querySelector('[data-okko-qo-modal]');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'okko-draft-modal okko-qo-modal';
      document.body.appendChild(dialog);
    }
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-okko-qo-close]')) dialog.close();
    });
    return dialog;
  }

  // --- Mini-guida con immagini (blocchi "Immagine guida" dall'editor) --------
  // L'embed data-okko-qo-guida lo emette okko-strumento-quickorder.liquid;
  // qui la galleria di miniature sotto l'area di incolla e la LIGHTBOX
  // (dialog separato: showModal dopo la modale QO -> sta sopra nel top layer).

  function guidaDati() {
    const embed = document.querySelector('[data-okko-qo-guida]');
    if (!embed) return null;
    try {
      const g = JSON.parse(embed.textContent);
      return (g && Array.isArray(g.items) && g.items.length) ? g : null;
    } catch (e) { return null; }
  }

  function guidaHtml() {
    const g = guidaDati();
    if (!g) return '';
    // il passo-video si riconosce dalla miniatura: badge play sopra la copertina
    const thumbs = g.items.map((it, i) => {
      const video = !!((it.v && it.v.length) || it.e);
      const play = video ? '<span class="okko-qo-guida__play" aria-hidden="true"><svg viewBox="0 0 16 16" fill="currentColor"><path d="M5.5 3.5l7 4.5-7 4.5z"/></svg></span>' : '';
      const etichetta = esc(it.d || g.t || 'Guida') + (video ? ' (video)' : '');
      return `<button type="button" class="okko-qo-guida__thumb${video ? ' is-video' : ''}" data-okko-qo-guida-apri="${i}" aria-label="${etichetta}"><span class="okko-qo-guida__num" aria-hidden="true">${i + 1}</span>${play}<img src="${esc(it.thumb)}" alt="" loading="lazy"></button>`;
    }).join('');
    return `
      <div class="okko-qo-guida">
        <p class="okko-qo-guida__titolo">${esc(g.t || 'Come funziona')}</p>
        <div class="okko-qo-guida__grid">${thumbs}</div>
      </div>`;
  }

  // La lightbox e' quella CONDIVISA del tema (window.OKKO.lightbox, okko-cart.js)
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-okko-qo-guida-apri]');
    if (!thumb) return;
    const g = guidaDati();
    if (!g || !window.OKKO || !window.OKKO.lightbox) return;
    window.OKKO.lightbox(
      g.items.map((it) => ({ full: it.full, d: it.d, v: it.v, e: it.e, thumb: it.thumb })),
      Number(thumb.dataset.okkoQoGuidaApri),
    );
  });

  function renderInput(message) {
    const d = ensureDialog();
    d.innerHTML = `
      <div class="okko-draft-modal__head">
        <h2 class="okko-draft-modal__title">${((window.OKKO || {}).testi || {}).ordineRapido || 'Ordine Rapido'} <small>${((window.OKKO || {}).testi || {}).ordineRapidoSub || 'dal gestionale al carrello'}</small></h2>
        <button type="button" class="okko-draft-modal__close" data-okko-qo-close aria-label="Chiudi"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg></button>
      </div>
      <div class="okko-qo-body${guidaDati() ? ' okko-qo-body--guida' : ''}">
        <div class="okko-qo-body__main">
          <textarea class="okko-qo-text" data-okko-qo-text rows="8" placeholder="Un articolo per riga: SKU e quantit\u00e0.&#10;&#10;ADUSBTYPC  10&#10;LAUNSM75AR  5"></textarea>
          <div class="okko-qo-drop" data-okko-qo-drop>
            <input type="file" accept=".csv,.tsv,.txt" data-okko-qo-file hidden>
            Trascina qui un file CSV oppure <button type="button" data-okko-qo-browse>scegli un file</button>
          </div>
          <p class="okko-qo-hint">Separatore libero (tab, punto e virgola, virgola o spazi); l'intestazione viene riconosciuta e saltata. Puoi incollare direttamente due colonne da Excel.</p>
          <p class="okko-foot-error" data-okko-qo-error ${message ? '' : 'hidden'}>${message ? esc(message) : ''}</p>
        </div>
        ${guidaHtml()}
      </div>
      <div class="okko-qo-foot">
        <button type="button" class="okko-btn okko-btn--ghost" data-okko-qo-catalog>Scarica catalogo</button>
        <div class="okko-qo-foot__right">
          <button type="button" class="okko-btn" data-okko-qo-preview>Anteprima</button>
        </div>
      </div>`;
    // Prefetch dell'indice mentre l'utente legge/incolla: le sonde view=sku/indice
    // sono pesanti e sequenziali (fix 01071b4). skuIndex serve sia all'Anteprima
    // (matching SKU) sia allo Scarica catalogo (select gia' pronti, niente attesa);
    // collectionsIndex al solo pannello catalogo. Ritornano promise in cache: e'
    // idempotente e non blocca (senza await, errori ignorati).
    skuIndex('all').catch(() => {});
    collectionsIndex().catch(() => {});
  }

  function renderPreview() {
    const d = ensureDialog();
    const ui = window.OKKO.ui;
    let pieces = 0;
    let total = 0;
    const body = rows.map((r, idx) => {
      const removeBtn = `<button type="button" class="okko-draft-row__remove" data-okko-qo-remove="${idx}" aria-label="Rimuovi">${ui.TRASH_SVG}</button>`;
      if (r.state === 'notfound') {
        return ui.modalRow({
          dead: true,
          title: r.sku,
          sku: `richiesti ${Number.isFinite(r.req) ? r.req : '?'} pz`,
          qtyHtml: '<div><span class="okko-qo-tag okko-qo-tag--warn">SKU non trovato</span></div>',
          actionsHtml: removeBtn,
        });
      }
      if (r.state === 'badqty') {
        return ui.modalRow({
          dead: true,
          img: r.e.i,
          url: r.e.u,
          title: r.e.t,
          variante: r.e.v,
          sku: r.e.s,
          qtyHtml: '<div><span class="okko-qo-tag okko-qo-tag--warn">quantit\u00e0 non valida</span></div>',
          actionsHtml: removeBtn,
        });
      }
      pieces += r.q;
      total += r.q * r.e.p;
      const clamped = r.q !== r.req; // anche in ECCESSO: la confezione puo' agganciare 30 -> 50
      return ui.modalRow({
        img: r.e.i,
        url: r.e.u,
        title: r.e.t,
        variante: r.e.v,
        sku: r.e.s,
        stock: r.e.m,
        pre: r.e.pre || 0,
        metaHtml: clamped ? `<span class="okko-qo-tag okko-qo-tag--warn">richiesti ${r.req}</span>` : '',
        prezzo: r.e.p, listino: r.e.c,
        priceHtml: ui.prezzoCell(r.e.p, r.e.c, r.e.op),
        qtyHtml: ui.stepperHtml({ qtyAttr: 'okko-qo-qty', stepAttr: 'okko-qo-step', id: idx, value: r.q, max: r.e.m, min: 0, conf: r.e.cf }),
        subHtml: ui.subCell(r.q * r.e.p, 'okko-qo-sub', idx),
        actionsHtml: removeBtn,
      });
    }).join('');

    const ok = rows.filter((r) => r.state === 'ok' && r.q > 0).length;
    // Indice parziale (pagine della sonda rotte): gli "SKU non trovato" possono
    // essere falsi negativi, l'utente deve saperlo prima di fidarsi dell'anteprima.
    const warn = indiceParziale
      ? `<p class="okko-foot-error">Attenzione: non ho potuto leggere tutto il catalogo (${indiceParziale} ${indiceParziale === 1 ? 'pagina' : 'pagine'} su cui il server ha dato errore). Alcuni SKU potrebbero risultare \u00abnon trovati\u00bb pur esistendo: ricarica la pagina e riprova.</p>`
      : '';
    d.innerHTML = `
      <div class="okko-draft-modal__head">
        <h2 class="okko-draft-modal__title">Anteprima <small data-okko-qo-count>${ok} ${ok === 1 ? 'articolo' : 'articoli'} \u00b7 ${pieces} pz</small></h2>
        <button type="button" class="okko-draft-modal__close" data-okko-qo-close aria-label="Chiudi"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg></button>
      </div>
      <div class="okko-qo-body">${warn}${body || '<p class="okko-empty">Niente da importare.</p>'}</div>
      <div class="okko-qo-foot">
        <div class="okko-qo-foot__totali">
          <span class="okko-qo-totali">Totale: <strong data-okko-qo-total>${money(total)}</strong> <small class="okko-nota-iva">${((window.OKKO || {}).testi || {}).ivaEscl || 'IVA escl.'}</small></span>
        </div>
        <div class="okko-qo-foot__right">
          <button type="button" class="okko-btn" data-okko-qo-add ${ok ? '' : 'disabled'}>${((window.OKKO || {}).testi || {}).aggiungiBozza || 'Aggiungi alla bozza'}</button>
        </div>
        <span class="okko-order-listino okko-order-listino--riga" data-okko-qo-listino>${listinoAgg()}</span>
      </div>`;
  }

  // valore di rivendita totale delle righe valide (helper condiviso di okko-cart.js)
  function listinoAgg() {
    const ui = (window.OKKO || {}).ui;
    if (!ui || !ui.listinoFraseHtml) return '';
    return ui.listinoFraseHtml(rows.filter((r) => r.state === 'ok').map((r) => ({ q: r.q, p: r.e.p, c: r.e.c })));
  }

  function refreshPreviewTotals() {
    let pieces = 0;
    let total = 0;
    let ok = 0;
    rows.forEach((r) => {
      if (r.state !== 'ok') return;
      pieces += r.q;
      total += r.q * r.e.p;
      if (r.q > 0) ok += 1;
    });
    const count = dialog.querySelector('[data-okko-qo-count]');
    if (count) count.textContent = `${ok} ${ok === 1 ? 'articolo' : 'articoli'} \u00b7 ${pieces} pz`;
    const tot = dialog.querySelector('[data-okko-qo-total]');
    if (tot) tot.textContent = money(total);
    const lst = dialog.querySelector('[data-okko-qo-listino]');
    if (lst) lst.innerHTML = listinoAgg();
  }

  async function buildRows(text) {
    const parsed = parseInput(text);
    if (!parsed.length) {
      renderInput('Niente da leggere: incolla o carica un file con SKU e quantit\u00e0.');
      return;
    }
    let index;
    try { index = await skuIndex(); }
    catch { renderInput("Impossibile scaricare l'indice del catalogo: riprova."); return; }
    indiceParziale = index.parziale || 0;
    rows = parsed.map((r) => {
      const found = index.get(r.sku);
      if (!found) return { state: 'notfound', sku: r.sku, req: r.req };
      const e = { ...found }; // clona: lo sconto muta e.p/e.o, non la cache dell'indice
      if (!Number.isFinite(r.req) || r.req <= 0) return { state: 'badqty', e, sku: r.sku };
      // clampQty: tetto stock + aggancio al multiplo di confezione (q < req -> tag "richiesti N")
      return { state: 'ok', e, sku: r.sku, req: r.req, q: window.OKKO.clampQty(r.req, e.m, e.cf || 0) };
    });
    await scontaRighe(rows); // prezzi personalizzati BSS sulle voci in anteprima
    renderPreview();
  }

  function writeDraft() {
    const list = rows
      .filter((r) => r.state === 'ok' && r.q > 0)
      .map((r) => ({ id: r.e.id, q: r.q, p: r.e.p, c: r.e.c, op: r.e.op, m: r.e.m, cf: r.e.cf || 0, pre: r.e.pre || 0, t: r.e.t, v: r.e.v, s: r.e.s, i: r.e.i, u: r.e.u }));
    if (!list.length) return;
    previewOrigine = null;
    window.OKKO.draftWrite(list);
    dialog.close();
    document.querySelector('[data-okko-draft-open]')?.click(); // revisione nel Riepilogo
  }

  // Riordino da "I miei ordini" (okko-cart.js): entra direttamente in ANTEPRIMA
  // con le righe dell'ordine passato — stessa revisione dell'import: variante
  // eliminata = "SKU non trovato", esaurita = quantita' a 0 col tag "richiesti",
  // clamp alla giacenza, stepper e rimozioni. Conferma = writeDraft.
  window.OKKO = window.OKKO || {};
  window.OKKO.qoPreview = (list) => {
    previewOrigine = 'ordini';
    rows = list.map((r) => {
      if (!r.id) return { state: 'notfound', sku: r.t || r.s || 'Articolo', req: r.q };
      const e = {
        id: r.id,
        p: r.p,
        m: r.disp ? (typeof r.m === 'number' ? r.m : null) : 0,
        c: typeof r.c === 'number' ? r.c : null,
        cf: typeof r.cf === 'number' && r.cf > 1 ? r.cf : 0, // confezione dall'embed ordini (quando la porta)
        pre: r.pre || 0, // pre-order dall'embed ordini (quando la porta)
        t: r.t, v: r.v || '', s: r.s, i: r.i, u: r.u,
      };
      return { state: 'ok', e, sku: r.s || '', req: r.q, q: window.OKKO.clampQty(r.q, e.m, e.cf) };
    });
    renderPreview();
    const d = ensureDialog();
    if (!d.open) d.showModal();
    // prezzi personalizzati BSS: sconta e ridisegna (il riordino parte dai prezzi
    // storici dell'ordine, che vanno riportati al prezzo B2B corrente)
    scontaRighe(rows).then(() => renderPreview());
  };

  // --- Scarica catalogo: filtri collezione/marca/modello + CSV o XLSX --------

  const catState = { handle: 'all', marca: '', modello: '', type: 'csv' };

  let collectionsPromise = null;

  /** Tutte le collezioni dalla vista /collections?view=indice (il for Liquid si ferma a 50). */
  function collectionsIndex() {
    if (collectionsPromise) return collectionsPromise;
    // Collezioni scelte nelle impostazioni tema (embed Liquid): il menu usa quelle.
    // Nessuna scelta -> elenco completo dalla vista-sonda /collections?view=indice.
    const custom = document.querySelector('[data-okko-collections-custom]');
    if (custom) {
      try {
        const items = JSON.parse(custom.textContent)
          .sort((a, b) => a.t.localeCompare(b.t, 'it'));
        collectionsPromise = Promise.resolve(items);
        return collectionsPromise;
      } catch { /* embed illeggibile: si passa alla sonda */ }
    }
    const ssKey = 'okko-collections-index';
    const cached = ssGet(ssKey);
    if (cached) { collectionsPromise = Promise.resolve(cached); return collectionsPromise; }
    collectionsPromise = (async () => {
      const url = (page) => okkoUrl(`/collections?view=indice${page > 1 ? `&page=${page}` : ''}`);
      const first = await fetchJson(url(1));
      const chunks = [first]; // sequenziale + retry, come skuIndex
      for (let page = 2; page <= Math.min(first.pages, 8); page += 1) {
        chunks.push(await fetchJson(url(page)));
      }
      const list = chunks.flatMap((c) => c.items)
        .map(([h, t]) => ({ h, t }))
        .sort((a, b) => a.t.localeCompare(b.t, 'it'));
      ssSet(ssKey, list); // persisti per la sessione
      return list;
    })();
    collectionsPromise.catch(() => { collectionsPromise = null; });
    return collectionsPromise;
  }

  function catalogHead() {
    return `
      <div class="okko-draft-modal__head">
        <h2 class="okko-draft-modal__title">Scarica catalogo <small>scegli cosa esportare</small></h2>
        <button type="button" class="okko-draft-modal__close" data-okko-qo-close aria-label="Chiudi"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg></button>
      </div>`;
  }

  /** Shell col LOADER: la form compare solo a dati pronti (populateCatalog).
      Al primo avvio la sonda impiega ~15s: la vecchia form con i select su
      "Caricamento\u2026" e Scarica gia' cliccabile sembrava rotta (2026-08-03). */
  function renderCatalog() {
    const d = ensureDialog();
    d.innerHTML = `
      ${catalogHead()}
      <div class="okko-qo-body okko-cat-loader" data-okko-cat-loader>
        <span class="okko-cat-loader__anello" aria-hidden="true"></span>
        <p class="okko-cat-loader__testo">Sto preparando il catalogo\u2026<br><small>al primo avvio possono servire circa 15 secondi</small></p>
      </div>
      <div class="okko-qo-foot">
        <button type="button" class="okko-btn okko-btn--ghost" data-okko-qo-back>Indietro</button>
      </div>`;
    populateCatalog();
  }

  /** La form vera, montata da populateCatalog quando indice e collezioni sono in mano. */
  function renderCatalogForm() {
    dialog.innerHTML = `
      ${catalogHead()}
      <div class="okko-qo-body">
        <label class="okko-cat-field">Collezione
          <select data-okko-cat-coll></select>
        </label>
        <label class="okko-cat-field">Marca
          <select data-okko-cat-marca></select>
        </label>
        <label class="okko-cat-field">Modello
          <select data-okko-cat-modello></select>
        </label>
        <div class="okko-cat-field">Formato
          <div class="okko-cat-types">
            <button type="button" class="okko-cat-type${catState.type === 'csv' ? ' is-on' : ''}" data-okko-cat-type="csv">CSV</button>
            <button type="button" class="okko-cat-type${catState.type === 'xlsx' ? ' is-on' : ''}" data-okko-cat-type="xlsx">XLSX</button>
          </div>
        </div>
        <p class="okko-foot-error" data-okko-qo-error hidden></p>
      </div>
      <div class="okko-qo-foot">
        <button type="button" class="okko-btn okko-btn--ghost" data-okko-qo-back>Indietro</button>
        <div class="okko-qo-foot__right">
          <button type="button" class="okko-btn" data-okko-cat-download>Scarica</button>
        </div>
      </div>`;
  }

  // Guardia anti-race dei menu: risponde solo l'ULTIMA chiamata. Senza, un
  // cambio rapido di collezione faceva sovrascrivere i menu dall'indice LENTO
  // della collezione abbandonata (visto live il 2026-08-03: "Tutto il catalogo"
  // selezionato coi soli marchi di "audio").
  let populateSeq = 0;

  /** Ricarica i menu (collezioni + marca/modello a cascata dall'indice scelto). */
  async function populateCatalog() {
    populateSeq += 1;
    const mio = populateSeq;
    // form gia' montata (cambio collezione): congela marca/modello e Scarica
    dialog.querySelectorAll('[data-okko-cat-marca], [data-okko-cat-modello], [data-okko-cat-download]')
      .forEach((el) => { el.disabled = true; });
    let index;
    let colls;
    try { [index, colls] = await Promise.all([skuIndex(catState.handle), collectionsIndex()]); }
    catch {
      if (mio !== populateSeq || !dialog.open) return;
      const loader = dialog.querySelector('[data-okko-cat-loader]');
      if (loader) {
        // primo avvio: la form non c'e' ancora \u2014 errore + Riprova al posto del loader
        loader.innerHTML = `
          <p class="okko-foot-error">Impossibile leggere il catalogo: riprova.</p>
          <button type="button" class="okko-btn" data-okko-cat-retry>Riprova</button>`;
        return;
      }
      const err = dialog.querySelector('[data-okko-qo-error]');
      if (err) { err.textContent = 'Impossibile leggere la collezione: riprova.'; err.hidden = false; }
      dialog.querySelectorAll('[data-okko-cat-download]').forEach((el) => { el.disabled = false; });
      return;
    }
    if (mio !== populateSeq || !dialog.open) return;
    // via il loader: da qui in poi la form esiste e si riempie in sincrono
    if (dialog.querySelector('[data-okko-cat-loader]')) renderCatalogForm();
    const collSel = dialog.querySelector('[data-okko-cat-coll]');
    const marcaSel = dialog.querySelector('[data-okko-cat-marca]');
    const modSel = dialog.querySelector('[data-okko-cat-modello]');
    if (!collSel || !marcaSel || !modSel) return;
    if (index.parziale) {
      // Non blocca (filtri e download restano usabili), ma l'export sarebbe monco.
      const err = dialog.querySelector('[data-okko-qo-error]');
      if (err) {
        err.textContent = `Attenzione: catalogo letto solo in parte (${index.parziale} ${index.parziale === 1 ? 'pagina' : 'pagine'} in errore): l'export sarebbe incompleto. Ricarica la pagina e riprova.`;
        err.hidden = false;
      }
    }
    const marche = new Set();
    const modelli = new Set();
    index.forEach((e) => {
      e.b.forEach((m) => marche.add(m));
      if (!catState.marca || e.b.includes(catState.marca)) e.mm.forEach((m) => modelli.add(m));
    });
    const options = (values, current, allLabel) => `<option value="">${allLabel}</option>${
      [...values].sort((x, y) => x.localeCompare(y, 'it'))
        .map((v) => `<option value="${esc(v)}" ${v === current ? 'selected' : ''}>${esc(v)}</option>`).join('')}`;
    collSel.innerHTML = `<option value="all">Tutto il catalogo</option>${
      colls.map((c) => `<option value="${esc(c.h)}" ${catState.handle === c.h ? 'selected' : ''}>${esc(c.t)}</option>`).join('')}`;
    marcaSel.innerHTML = options(marche, catState.marca, 'Tutte le marche');
    modSel.innerHTML = options(modelli, catState.modello, 'Tutti i modelli');
    collSel.disabled = false;
    marcaSel.disabled = false;
    modSel.disabled = false;
    const dl = dialog.querySelector('[data-okko-cat-download]');
    if (dl) dl.disabled = false;
  }

  function triggerDownload(blob, filename) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function doCatalogDownload(btn) {
    const label = btn.textContent;
    const err = dialog.querySelector('[data-okko-qo-error]');
    if (err) err.hidden = true;
    btn.disabled = true;
    try {
      // Il sito B2B e' solo-loggati: i prezzi personalizzati BSS DEVONO esserci.
      // Prima di generare il file aspettiamo che il ponte sia PRONTO (oggetto BSS +
      // JSON regole + cliente), con un loader informativo. Se BSS non arriva entro il
      // cap, ABORTIAMO con un messaggio: meglio far ripetere il click che consegnare
      // un CSV a prezzo pieno che il checkout poi non onorerebbe.
      const bss = window.OKKO && window.OKKO.bss;
      if (bss) {
        btn.textContent = 'Carico i prezzi personalizzati\u2026';
        const pronto = await bss.attendiPronto(20000);
        if (!pronto) {
          if (err) {
            err.textContent = 'Prezzi personalizzati non ancora disponibili: attendi qualche secondo e riprova.';
            err.hidden = false;
          }
          return;
        }
      }
      btn.textContent = 'Preparazione\u2026';
      const index = await skuIndex(catState.handle);
      if (index.parziale) {
        // Un export monco spacciato per completo e' peggio di nessun export: si abortisce.
        if (err) {
          err.textContent = `Catalogo letto solo in parte (${index.parziale} ${index.parziale === 1 ? 'pagina' : 'pagine'} in errore): export annullato per non produrre un file incompleto. Ricarica la pagina e riprova.`;
          err.hidden = false;
        }
        return;
      }
      // nome articolo dell'export: ricompone prodotto + variante (la sonda ora li
      // tiene separati). Trattino SEMPLICE: quello lungo e' vietato dal ToV \u00a75.2.
      const nomeArticolo = (e) => (e.v ? `${e.t} - ${e.v}` : e.t);
      const out = [...index.values()]
        .filter((e) => (!catState.marca || e.b.includes(catState.marca))
          && (!catState.modello || e.mm.includes(catState.modello)))
        .sort((x, y) => String(x.s).localeCompare(String(y.s)));
      const suffix = [catState.handle !== 'all' ? catState.handle : '', catState.marca, catState.modello]
        .filter(Boolean).join('-').toLowerCase().replace(/[^a-z0-9-]+/g, '-');
      const name = `catalogo-okko${suffix ? `-${suffix}` : ''}`;
      // Metadati BSS (tag/collezioni) per lo sconto dell'export, dalla cache a sessione.
      // Lo sconto e' risolto da scontaPrezzo con le cpRules LIVE (anche per-collezione);
      // pieno se la variante non ha regola (prezzo reale del cliente, non un errore).
      const meta = (bss && bss.pronto) ? await bssMeta(out.map((e) => e.pid)) : new Map();
      // Colonne del file. SKU e Quantit\u00e0 Ordine sono ADIACENTI (dopo il Nome)
      // cosi' il cliente puo' copiare solo quelle due e re-incollarle nell'Ordine
      // Rapido. Cifre: e.p = listino, e.c = consigliato (compare_at), scontato
      // dalle regole, ricarico = gemello JS di okko-ricarico.liquid.
      // Immagine: URL a width 1200, protocollo https.
      const iva = (window.OKKO && window.OKKO.iva) || 22;
      const scontato = (e) => {
        if (!bss || !bss.pronto) return null;
        const m = meta.get(String(e.pid)) || { t: [], c: [] };
        const s = bss.scontaPrezzo({ id: e.pid, tags: m.t, collections: m.c, variantId: e.id }, e.p);
        return s < e.p ? s : null;
      };
      // "Prezzo Scontato" compare SOLO se nel file c'e' ALMENO un valore (deciso
      // sui dati veri, non sul solo "ha regole": un export filtrato puo' non
      // toccare nessuna riga scontata \u2014 richiesta 2026-08-03). Pre-calcolo qui,
      // una volta sola per riga.
      const scontati = new Map();
      out.forEach((e) => { const s = scontato(e); if (s != null) scontati.set(e, s); });
      const ricarico = (e) => {
        if (!e.c || e.p <= 0) return '';
        const netto = e.c / (1 + iva / 100);
        // soglia sul valore ARROTONDATO (>= x1,0), come okko-ricarico.liquid (2026-08-03)
        const d = Math.round((netto * 10) / e.p);
        if (d < 10) return '';
        return `\u00D7${Math.floor(d / 10)},${d % 10}`;
      };
      const consigliato = (e) => (e.c && e.c > e.p ? e.c : null);
      const immagine = (e) => { let u = e.i || ''; if (!u) return ''; if (u.startsWith('//')) u = `https:${u}`; return u.replace(/width=\d+/, 'width=1200'); };
      const cols = [
        { h: 'Articolo', v: (e) => nomeArticolo(e), t: 'str' },
        { h: 'SKU', v: (e) => e.s, t: 'str' },
        { h: 'Quantit\u00e0 Ordine', v: () => '', t: 'raw' },
        { h: 'Disponibili', v: (e) => (e.m === null ? '' : e.m), t: 'raw' },
        { h: 'Prezzo Listino', v: (e) => e.p, t: 'money' },
        ...(scontati.size ? [{ h: 'Prezzo Scontato', v: (e) => (scontati.has(e) ? scontati.get(e) : null), t: 'money' }] : []),
        { h: 'Ricarico', v: ricarico, t: 'str' },
        { h: 'Prezzo Consigliato', v: consigliato, t: 'money' },
        { h: 'Immagine', v: immagine, t: 'str' }, // in fondo: dato di reference, non di ordine
      ];
      if (catState.type === 'xlsx') {
        const rowsOut = out.map((e) => cols.map((c) => {
          const val = c.v(e);
          return c.t === 'money' ? (val == null ? '' : val / 100) : val;
        }));
        triggerDownload(xlsxBlob(cols.map((c) => c.h), rowsOut), `${name}.xlsx`);
      } else {
        const cell = (c, e) => {
          const val = c.v(e);
          if (c.t === 'money') return val == null ? '' : (val / 100).toFixed(2).replace('.', ',');
          return String(val).replace(/;/g, ',');
        };
        const lines = [cols.map((c) => c.h).join(';')];
        out.forEach((e) => lines.push(cols.map((c) => cell(c, e)).join(';')));
        // BOM: senza, Excel apre il CSV con gli accenti rotti
        triggerDownload(new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }), `${name}.csv`);
      }
    } catch {
      if (err) { err.textContent = 'Download non riuscito: riprova.'; err.hidden = false; }
    } finally {
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  // --- XLSX minimale senza librerie -----------------------------------------
  // Un .xlsx e' uno zip di XML: qui lo zip e' "stored" (nessuna compressione,
  // bastano CRC32 e header scritti a mano) attorno a un foglio SpreadsheetML
  // con celle inline. Excel, Numbers e LibreOffice lo aprono senza avvisi.

  let crcTable = null;

  function crc32(data) {
    if (!crcTable) {
      crcTable = new Uint32Array(256);
      for (let i = 0; i < 256; i += 1) {
        let c = i;
        for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        crcTable[i] = c >>> 0;
      }
    }
    let crc = 0xffffffff;
    for (let i = 0; i < data.length; i += 1) crc = crcTable[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
  }

  function zipStore(files) {
    const enc = new TextEncoder();
    const parts = [];
    const central = [];
    let offset = 0;
    files.forEach((f) => {
      const nameB = enc.encode(f.name);
      const data = enc.encode(f.data);
      const crc = crc32(data);
      const h = new DataView(new ArrayBuffer(30));
      h.setUint32(0, 0x04034b50, true);
      h.setUint16(4, 20, true);
      h.setUint16(6, 0x0800, true); // flag utf-8
      h.setUint32(14, crc, true);
      h.setUint32(18, data.length, true);
      h.setUint32(22, data.length, true);
      h.setUint16(26, nameB.length, true);
      parts.push(new Uint8Array(h.buffer), nameB, data);
      central.push({ nameB, crc, size: data.length, offset });
      offset += 30 + nameB.length + data.length;
    });
    const centralStart = offset;
    central.forEach((c) => {
      const h = new DataView(new ArrayBuffer(46));
      h.setUint32(0, 0x02014b50, true);
      h.setUint16(4, 20, true);
      h.setUint16(6, 20, true);
      h.setUint16(8, 0x0800, true);
      h.setUint32(16, c.crc, true);
      h.setUint32(20, c.size, true);
      h.setUint32(24, c.size, true);
      h.setUint16(28, c.nameB.length, true);
      h.setUint32(42, c.offset, true);
      parts.push(new Uint8Array(h.buffer), c.nameB);
      offset += 46 + c.nameB.length;
    });
    const eocd = new DataView(new ArrayBuffer(22));
    eocd.setUint32(0, 0x06054b50, true);
    eocd.setUint16(8, files.length, true);
    eocd.setUint16(10, files.length, true);
    eocd.setUint32(12, offset - centralStart, true);
    eocd.setUint32(16, centralStart, true);
    parts.push(new Uint8Array(eocd.buffer));
    return new Blob(parts, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  function xlsxBlob(headers, dataRows) {
    const xesc = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const cell = (v) => {
      if (v === '' || v === null || v === undefined) return '<c/>';
      if (typeof v === 'number') return `<c><v>${v}</v></c>`;
      return `<c t="inlineStr"><is><t xml:space="preserve">${xesc(v)}</t></is></c>`;
    };
    const row = (cells) => `<row>${cells.map(cell).join('')}</row>`;
    const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${row(headers)}${dataRows.map(row).join('')}</sheetData></worksheet>`;
    const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Catalogo" sheetId="1" r:id="rId1"/></sheets></workbook>`;
    const wbRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`;
    const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
    const types = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`;
    return zipStore([
      { name: '[Content_Types].xml', data: types },
      { name: '_rels/.rels', data: rels },
      { name: 'xl/workbook.xml', data: workbook },
      { name: 'xl/_rels/workbook.xml.rels', data: wbRels },
      { name: 'xl/worksheets/sheet1.xml', data: sheet },
    ]);
  }

  function readFile(file) {
    const reader = new FileReader();
    reader.onload = () => buildRows(String(reader.result));
    reader.readAsText(file);
  }

  // --- Eventi -----------------------------------------------------------------

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-okko-quickorder-open]')) {
      previewOrigine = null; // apertura normale: niente contesto riordino
      renderInput();
      ensureDialog().showModal();
      skuIndex(); // prefetch mentre l'utente incolla
      return;
    }
    // Trigger globale (card homepage): apre direttamente la schermata
    // "Scarica catalogo" dello strumento, senza passare dall'incolla.
    if (e.target.closest('[data-okko-qo-catalogo-open]')) {
      previewOrigine = null;
      renderCatalog();
      ensureDialog().showModal();
      return;
    }
    if (!dialog || !dialog.open) return;
    if (e.target.closest('[data-okko-qo-browse]')) { dialog.querySelector('[data-okko-qo-file]').click(); return; }
    if (e.target.closest('[data-okko-qo-preview]')) { buildRows(dialog.querySelector('[data-okko-qo-text]').value); return; }
    // Nell'ANTEPRIMA non ci sono piu' ne' Svuota ne' Indietro (2026-07-28,
    // Nicolas): per abbandonare basta chiudere la modale, si riapre pulita.
    // Questo back serve al pannello "Scarica catalogo"; il ramo riordino resta
    // per difesa (previewOrigine si azzera comunque alla chiusura del flusso).
    if (e.target.closest('[data-okko-qo-back]')) {
      if (previewOrigine === 'ordini') {
        rows = [];
        previewOrigine = null;
        dialog.close();
        document.querySelector('[data-okko-ordini-modal]')?.showModal();
        return;
      }
      renderInput();
      return;
    }
    if (e.target.closest('[data-okko-qo-add]')) { writeDraft(); return; }
    if (e.target.closest('[data-okko-qo-catalog]')) { renderCatalog(); return; }
    // sonda fallita al primo avvio: si riparte dal loader (la cache dell'indice
    // si e' gia' svuotata da sola sull'errore, il retry rifetcha davvero)
    if (e.target.closest('[data-okko-cat-retry]')) { renderCatalog(); return; }
    const typeBtn = e.target.closest('[data-okko-cat-type]');
    if (typeBtn) {
      catState.type = typeBtn.dataset.okkoCatType;
      dialog.querySelectorAll('[data-okko-cat-type]').forEach((b) => b.classList.toggle('is-on', b === typeBtn));
      return;
    }
    const dl = e.target.closest('[data-okko-cat-download]');
    if (dl) { doCatalogDownload(dl); return; }
    const step = e.target.closest('[data-okko-qo-step]');
    if (step) {
      const input = step.parentElement.querySelector('[data-okko-qo-qty]');
      const conf = Number(input.dataset.okkoConfezione) || 0;
      const inc = conf || (window.OKKO.passo ? window.OKKO.passo() : 1); // confezione: avanza di N
      input.value = String((Number(input.value) || 0) + Number(step.dataset.okkoQoStep) * inc);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true })); // riaggancia se si partiva da un non-multiplo
      return;
    }
    const remove = e.target.closest('[data-okko-qo-remove]');
    if (remove) {
      rows.splice(Number(remove.dataset.okkoQoRemove), 1);
      renderPreview();
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-okko-qo-qty]')) {
      const row = rows[Number(e.target.dataset.okkoQoQty)];
      if (!row) return;
      const max = row.e.m === null ? Infinity : row.e.m;
      row.q = Math.max(0, Math.min(max, Math.floor(Number(e.target.value) || 0)));
      e.target.value = String(row.q);
      const sub = dialog.querySelector(`[data-okko-qo-sub="${e.target.dataset.okkoQoQty}"]`);
      if (sub) sub.textContent = money(row.q * row.e.p);
      refreshPreviewTotals();
      return;
    }
  });

  document.addEventListener('change', (e) => {
    // Fine digitazione in Anteprima: aggancio della quantita' alla confezione della riga.
    if (e.target.matches('[data-okko-qo-qty]')) {
      const row = rows[Number(e.target.dataset.okkoQoQty)];
      if (!row || !row.e.cf) return;
      const q = window.OKKO.clampQty(e.target.value, row.e.m, row.e.cf);
      if (q === row.q && String(q) === e.target.value) return;
      row.q = q;
      e.target.value = String(q);
      const sub = dialog.querySelector(`[data-okko-qo-sub="${e.target.dataset.okkoQoQty}"]`);
      if (sub) sub.textContent = money(row.q * row.e.p);
      refreshPreviewTotals();
      return;
    }
    if (e.target.matches('[data-okko-qo-file]') && e.target.files[0]) { readFile(e.target.files[0]); return; }
    if (e.target.matches('[data-okko-cat-coll]')) {
      catState.handle = e.target.value;
      catState.marca = '';
      catState.modello = '';
      populateCatalog();
      return;
    }
    if (e.target.matches('[data-okko-cat-marca]')) {
      catState.marca = e.target.value;
      catState.modello = '';
      populateCatalog();
      return;
    }
    if (e.target.matches('[data-okko-cat-modello]')) catState.modello = e.target.value;
  });

  // Drag & drop sul riquadro
  document.addEventListener('dragover', (e) => {
    const drop = e.target.closest ? e.target.closest('[data-okko-qo-drop]') : null;
    if (!drop) return;
    e.preventDefault();
    drop.classList.add('is-over');
  });
  document.addEventListener('dragleave', (e) => {
    if (e.target.closest) e.target.closest('[data-okko-qo-drop]')?.classList.remove('is-over');
  });
  document.addEventListener('drop', (e) => {
    const drop = e.target.closest ? e.target.closest('[data-okko-qo-drop]') : null;
    if (!drop) return;
    e.preventDefault();
    drop.classList.remove('is-over');
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) readFile(file);
  });

  // Prefetch dell'indice UNA VOLTA per sessione, in sottofondo e DOPO BSS (per non
  // competere col suo caricamento). Al primo load riempie la cache a sessione, cosi'
  // Anteprima e Scarica catalogo sono istantanei su questa e sulle pagine successive.
  // Su sessione gia' calda skuIndex/collectionsIndex leggono sessionStorage: 0 fetch.
  window.addEventListener('load', () => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const go = () => idle(async () => {
      collectionsIndex().catch(() => {});
      const index = await skuIndex('all').catch(() => null);
      // Scalda i METADATI BSS (tag/collezioni) di tutto il catalogo nella cache a
      // sessione, cosi' il click su Scarica e l'Anteprima sono quasi istantanei. E'
      // cache di DOWNLOAD/ANTEPRIMA, non del live-site; lo sconto resta risolto live.
      if (index) {
        const pids = [...new Set([...index.values()].map((e) => e.pid).filter(Boolean))];
        bssMeta(pids).catch(() => {});
      }
    });
    const bss = window.OKKO && window.OKKO.bss;
    if (bss && bss.attendiPronto) bss.attendiPronto(20000).finally(go); // dopo BSS pronto (o cap)
    else setTimeout(go, 2000); // ponte assente: prefetch comunque, ma non subito
  });
})();
